/**
 * **D242 — Android's address-book list, rendered as a screen phase rather than an overlay — and
 * since D340 the iPhone's too**, Apple's picker having no search field and no API to add one. The
 * paragraphs below say *Android* where D242 wrote them; every rule in them now holds on both.
 *
 * Android ships no permission-free multi-select contact intent, so where iOS gets its plural
 * delegate for nothing the multi path there is a `READ_CONTACTS` read of the book and a list of
 * our own. That list is *this* file — React rather than a native activity, because an
 * Android-only selection surface would put it in a fourth language, unreadable by every test
 * here and free to diverge on RTL, Hebrew collation and search: the *"a defect fixed in one leaf
 * is fixed in one"* failure, bought voluntarily.
 *
 * **It is a phase, not an overlay, and the name `…Sheet` is the one thing here that misleads.**
 * The screen's `Phase` union is rendered by early return and this is its fourth member: there is
 * no backdrop, nothing is layered over anything, and this file reaches for no overlay primitive of
 * its own.
 *
 * **Back is answered from the screen, and only behind a gesture may it be.** `InviteScreen`
 * registers one `useDismissOnBack` entry for this phase, so the phone's back gesture raises the
 * same leave-confirm the app bar's chevron does instead of abandoning the whole screen with the
 * ticks in it. That was refused while the sentence above read *"nothing here reaches for
 * `useDismissOnBack`"*, and the refusal was right for the reason it gave — an overlay pushing a
 * history entry with no gesture behind it is marked **skippable** by Chrome, which is D145's own
 * stated failure. D242 answered it by making the tap on *Add from contacts* the only way into this
 * phase. **D250 adds a second way in that carries no tap** — where `READ_CONTACTS` is already
 * granted the book opens itself at mount, there being no dialog left to raise out of context — so
 * the rule moved from *which phase* to *which push*: the entry is registered on entry for a
 * tap-opened book and on the manager's first tick for an auto-opened one. The old rule read **do
 * not lift that entry to a mode that auto-opens**; what replaced it is narrower rather than
 * weaker — **do not push it without a gesture.** Every other mode never reaches this phase at all.
 *
 * **The book lives in props that come from component state and dies with the phase — never a
 * module-level variable.** That is the reflexive "don't re-read the book every time" optimization,
 * and it is forbidden by name: a module-level copy outlives the screen, the gate, and the
 * manager's decision to leave, and it is what would make *"the address book is never stored"*
 * false. `infra/tests/web-build-hygiene.test.ts` holds this file to that, and to the absence of
 * every browser-storage and network symbol this repository can name — the compliance no-moves
 * rest on that guard rather than on this comment. **Which is also why no such symbol is written
 * out anywhere above:** that guard scans this file's source text, so a forbidden name quoted in a
 * comment is a red suite and a reader who cannot tell a prohibition from a violation.
 *
 * **Nothing but the chosen numbers leaves this component.** A picked contact's *name* is display
 * only: it is what lets a manager check twenty rows against who they meant to invite, and it is
 * deliberately not carried into `memberLabel`, which is a declared User Content type — filling it
 * from the address book would silently convert third-party content into collected, transmitted
 * data and falsify a store form.
 *
 * **D346 (S1) moves Add to the app bar and states the cap again.** The footer and its `onAdd` prop
 * retired — the screen adds `onSelectionChange`'s own report, so there is still exactly one list
 * that is asked about and added. *Selected: N* is gone with the footer, and so — after the device
 * pass — is D346 (c)'s `bulkBookSelectedOf` line that briefly restated the cap: the strip already
 * shows who is picked, and the cap is back to D245's enforced-but-unstated. Rows gained an avatar
 * (`ContactAvatar`, no tick badge — the checkbox is the one mark of state), and — while the search is empty and the
 * book's own order sorts consistently under one — a letter header per run (§4.3; not drawn at all
 * over an inconsistent order, never jumbled). **A tick made during a non-empty search clears the
 * query and refocuses the field**, so the next name can be typed with the keyboard already up.
 *
 * **D346 (S2) — a contact with several numbers asks which, and a number already on this gate
 * cannot be picked.** This list used to tick `phones[0]` and nothing else, so a contact's second
 * number could never be reached from it and a contact whose first stored number was not their
 * mobile was silently invited on the wrong one. That half of the defect is closed here: a row with
 * two or more numbers carries an *N numbers* tag, and its unticked checkbox is **controlled** —
 * the change opens `NumberChooser` instead of ticking, so a dismissed chooser leaves it exactly as
 * unchecked as it was. Ticks stay keyed by `E164`, so a number two contacts share ticks both rows
 * and is invited once. **`members` is the gate's live grants, handed in by prop** (the screen
 * reads the roster; this file may not import the transport or the cache, and
 * `web-build-hygiene.test.ts` is what holds that): a contact greys only when **every** number it
 * carries is on the gate, its on-gate numbers are disabled inside the chooser otherwise, and a
 * row ticked before the roster lands **stays ticked** — nothing changes under the manager's thumb.
 * An empty set (a roster not yet arrived, or failed) greys nothing, and the server's
 * `skipped-duplicate` stays the backstop.
 *
 * **What remains of the first-number defect is outside this list.** `InviteScreen`'s `runPicker`
 * — the PWA's Chrome multi-picker, and the iPhone's Apple-picker fallback when Contacts access is
 * *None* — still takes `phones[0]` of each picked contact: both are OS pickers with no list of
 * ours to ask in (plan §7), and D270 de-prioritised the PWA.
 *
 * **The chooser is a `.popover` sibling, by D144's split-by-what-it-is rule** — a list the manager
 * picks from, which is `LanguagePopover`'s shape, not `Popover`'s input, `ConfirmPopover`'s yes/no
 * or `AlertPopover`'s answer (`NumberChooser.tsx` says why at length). It is opened by a tap, so
 * D145 lets it own a `useDismissOnBack` entry of its own, pushed **above** the book's: one back
 * closes the chooser and leaves the book standing with its ticks, a second raises D245's
 * leave-confirm. 🔸 On an auto-opened book whose first gesture is the tap that opens the chooser,
 * the book's own entry is not yet armed (D250 waits for a tick), so the chooser's entry is the only
 * one — the same bounded cost D250 already states for a back pressed before the first tick.
 */
import { useEffect, useMemo, useRef, useState } from 'react'
import type { E164 } from '@selogate/core'
import { FrameOverlay, Icon, LinkButton } from '../../components'
import {
  contactPhoneSelections,
  type PhoneKind,
  type PickedContact,
} from '../../device-capabilities/contacts'
import type { Key } from '../../i18n/catalogs'
import { useDismissOnBack } from '../../router'
import { ContactAvatar, firstGrapheme, isLetterGrapheme } from './ContactAvatar'
import { NumberChooser, PHONE_KIND_KEYS } from './NumberChooser'

export interface ContactBookSelection {
  readonly phone: E164
  readonly name?: string
  /** D346 (f) — the chosen number's type, display only; never reaches a request body. */
  readonly kind?: PhoneKind
}

export interface ContactBookSheetProps {
  /** The book, held by the caller's component state for exactly as long as this phase is mounted. */
  readonly contacts: readonly PickedContact[]
  /**
   * D340 — iOS 18's limited grant. **Stated, unlike the cap**: the cap is a rule the manager meets
   * by ticking, while a partial book is a fact about the list itself, and a list of twelve with no
   * sentence reads as a broken address book — the lie D242's parser refuses one layer down.
   */
  readonly limited?: boolean
  /**
   * D343 — the way to a contact the limited grant does not hold: this app's page in Settings,
   * where the manager shares more or allows every contact. **Drawn only beside the limited line**:
   * without it a manager who shared a few contacts could reach nobody else from this list, the
   * auto-open landing them here every time. D341's way was Apple's own button in a native sheet,
   * which is blank or dead on iOS 26+ hardware.
   *
   * **The list stays mounted across it**, so the ticks survive without being carried anywhere: the
   * caller re-reads the book on the manager's return and hands back new `contacts`, and a tick is
   * keyed by its number, which the re-read does not change. **It resolves `true` when the book was
   * replaced, and the search is then CLEARED**: the person just shared was found in Settings, not
   * by this field, so a query typed before the trip would filter them out of the list they were
   * shared into and read as a share that failed. D341 reset the field to the sheet's text for the
   * same reason.
   */
  readonly onShareMore?: () => Promise<boolean>
  /**
   * `MAX_BULK_ROWS − entries.length`. **Enforced, and — since D346 — stated again**: the count
   * line above the list reads *N of {{max}} selected*, `max` being this same value, so the number
   * a manager sees is the number that actually caps the ticking.
   */
  readonly remaining: number
  /**
   * Reported upward on every tick, because the control that leaves this list — the app bar's
   * chevron and the phone's back gesture — is the screen's, not this component's, and it has to
   * know whether there is a selection worth stopping for. **Since D346 it is also what feeds the
   * app bar's own Add**: the footer and its `onAdd` prop retired, so this is the only channel the
   * selection travels through. The selection itself stays here: the phone→name resolution is built
   * from `contacts`, and splitting the ticks from the mapping puts half of one answer in each
   * component.
   */
  readonly onSelectionChange: (selections: readonly ContactBookSelection[]) => void
  /**
   * D346 (g) — the numbers holding a **live grant** on this gate, already normalized to `E164` by
   * the screen that read the roster. By prop only: this file renders the book and may not reach
   * the network. Empty until the roster lands, and empty for good if it failed — which greys
   * nothing, by design (fails open; the server's `skipped-duplicate` is the backstop).
   */
  readonly members: ReadonlySet<E164>
  readonly t: (key: Key<'invite'>, vars?: Readonly<Record<string, string | number>>) => string
}

/**
 * **One tick: the number, and the row it was ticked on.** The number is the invite — a number two
 * contacts share is one pick, ticks both rows and adds once (§4.6's S2 test list) — while the row
 * is whose name and type the pick carries. Keying the name off the number alone gave a shared
 * line to whichever contact the book happened to list first, so picking it on *Levi home* listed
 * it under *Dana Levi* in the strip, the collect step and the invite.
 */
interface Pick {
  readonly phone: E164
  readonly rowKey: string
}

interface BookRow {
  readonly key: string
  /** Every dialable number the contact carries, de-duplicated by `E164`, in the book's order. */
  readonly phones: readonly E164[]
  /** Aligned 1:1 with `phones` when the surface supplied types (S3); absent on the PWA. */
  readonly kinds?: readonly PhoneKind[]
  readonly name?: string
  /** Pre-lowered once, so typing does not re-case the whole book on every keystroke. */
  readonly haystack: string
}

function kindOf(row: BookRow, phone: E164): PhoneKind | undefined {
  const index = row.phones.indexOf(phone)
  return index === -1 ? undefined : row.kinds?.[index]
}

/**
 * §4.3 — the first grapheme of the display name, upper-cased; anything not a letter is `#`. The
 * grapheme and the letter test are the avatar's own (`ContactAvatar.tsx`), so a row's header and
 * its initial cannot disagree about what the name's first letter is — and the one segmenter is
 * built once and guarded there, rather than once per row here.
 */
function headerFor(displayName: string): string {
  const upper = (firstGrapheme(displayName) ?? '').toLocaleUpperCase()
  return isLetterGrapheme(upper) ? upper : '#'
}

/**
 * §4.3 — headers only where the book's own order draws them consistently: run-length-encode the
 * header of each row in order, and refuse the whole set the moment one letter reopens after
 * closing (an iPhone sorted by surname but displayed first-name-first, say). `undefined` means
 * *draw none*; otherwise the map from a row's key to "this row starts a new header block".
 */
function consistentHeaders(rows: readonly BookRow[]): ReadonlyMap<string, string> | undefined {
  const starts = new Map<string, string>()
  const seenBlocks = new Set<string>()
  let previous: string | undefined
  for (const row of rows) {
    const header = headerFor(row.name ?? row.phones[0] ?? '')
    if (header !== previous) {
      if (seenBlocks.has(header)) return undefined
      seenBlocks.add(header)
      starts.set(row.key, header)
      previous = header
    }
  }
  return starts
}

export function ContactBookSheet({
  contacts,
  limited = false,
  onShareMore,
  remaining,
  onSelectionChange,
  members,
  t,
}: ContactBookSheetProps): JSX.Element {
  const [query, setQuery] = useState('')
  const [selected, setSelected] = useState<readonly Pick[]>([])
  /** D343 — Settings is in front, so a second tap cannot queue a second trip behind it. */
  const [sharingMore, setSharingMore] = useState(false)
  /**
   * D346 (e) — the row whose chooser is open, by its key rather than the row itself: a re-read of
   * the book (D343's return from Settings) replaces the rows, and a chooser holding a stale row
   * would offer numbers the book no longer has. A key the new book lacks simply closes it.
   */
  const [chooserKey, setChooserKey] = useState<string | undefined>(undefined)
  const searchInputRef = useRef<HTMLInputElement>(null)
  const stripRef = useRef<HTMLDivElement>(null)

  /**
   * **One row per contact, carrying every number it has.** `contactPhoneSelections` drops a
   * contact carrying nothing this flow can dial, which is an ordinary address-book entry rather
   * than a fault, and de-duplicates the rest by `E164` (first kind wins, S3). The key stays
   * `${index}:${phones[0]}` (§4.1); search matches the name or **any** of the numbers.
   */
  const rows = useMemo<readonly BookRow[]>(
    () =>
      contactPhoneSelections(contacts).flatMap((selection, index) => {
        const first = selection.phones[0]
        if (first === undefined) return []
        return [
          {
            key: `${index}:${first}`,
            phones: selection.phones,
            ...(selection.kinds === undefined ? {} : { kinds: selection.kinds }),
            ...(selection.name === undefined ? {} : { name: selection.name }),
            haystack: `${selection.name ?? ''} ${selection.phones.join(' ')}`.toLowerCase(),
          },
        ]
      }),
    [contacts],
  )

  /** §4.3 — computed once over the book's own order, independent of the current search. */
  const headerStarts = useMemo(() => consistentHeaders(rows), [rows])

  /**
   * Which row a pick is listed under, for its name and its kind: **the row it was ticked on**
   * ({@link Pick}). The first row carrying the number is only the fallback, for a pick whose row
   * a re-read of the book (D343's return from Settings) has re-keyed.
   */
  const rowOf = useMemo(() => {
    const byKey = new Map<string, BookRow>()
    const byPhone = new Map<E164, BookRow>()
    for (const row of rows) {
      byKey.set(row.key, row)
      for (const phone of row.phones) if (!byPhone.has(phone)) byPhone.set(phone, row)
    }
    return (pick: Pick): BookRow | undefined => {
      const row = byKey.get(pick.rowKey)
      return row !== undefined && row.phones.includes(pick.phone) ? row : byPhone.get(pick.phone)
    }
  }, [rows])

  /**
   * The one place a ticked `E164` becomes a `ContactBookSelection`. Both exits read it — the app
   * bar's Add, and the report the screen's leave-confirm acts on — so the list the manager is
   * asked about and the list that is added cannot be two different lists.
   */
  const resolved = useMemo<readonly ContactBookSelection[]>(
    () =>
      selected.flatMap((pick) => {
        const { phone } = pick
        const row = rowOf(pick)
        if (row === undefined) return []
        const kind = kindOf(row, phone)
        return [
          {
            phone,
            ...(row.name === undefined ? {} : { name: row.name }),
            ...(kind === undefined ? {} : { kind }),
          },
        ]
      }),
    [rowOf, selected],
  )

  /* Reported after the render that produced it, never from inside `toggle` — a caller's `setState`
     run during this component's own event handler is a second update in the same batch, and the
     screen would be told about a selection this component had not finished making. */
  useEffect(() => {
    onSelectionChange(resolved)
    // `onSelectionChange` is a fresh closure per parent render; depending on it would re-report on
    // every keystroke in the search field. The selection is what this effect is about.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [resolved])

  /**
   * D346 (h) — newest pick scrolled into view at the inline end, in the strip's own scroller.
   * jsdom has no layout and no `scrollIntoView`, so this is a no-op there and a device-pass
   * mechanic everywhere else (§6.5).
   */
  useEffect(() => {
    const el = stripRef.current
    if (el === null || selected.length === 0) return
    const last = el.lastElementChild
    if (last !== null && typeof last.scrollIntoView === 'function') {
      last.scrollIntoView({ inline: 'end', block: 'nearest' })
    }
  }, [selected])

  const trimmed = query.trim().toLowerCase()
  const visible = trimmed === '' ? rows : rows.filter((row) => row.haystack.includes(trimmed))

  const chosen = new Set(selected.map((pick) => pick.phone))
  /**
   * **The cap counts picks, and a pick is one contact on one number.** A contact with three
   * numbers costs one slot, the same as a contact with one, because only the number the manager
   * chose is ticked — which is what makes `remaining` (bulk rows, one per number) and the picked
   * strip agree.
   */
  const full = chosen.size >= remaining

  /** D346 (e) — the numbers of a row that could still be ticked: not already on this gate. */
  function eligibleOf(row: BookRow): readonly E164[] {
    return row.phones.filter((phone) => !members.has(phone))
  }

  /** Ticks one number. The single path every tick takes — a row's own, and a chooser's pick. */
  function tick(phone: E164, row: BookRow): void {
    if (chosen.has(phone) || full) return
    setSelected((prev) =>
      prev.some((pick) => pick.phone === phone) || prev.length >= remaining
        ? prev
        : [...prev, { phone, rowKey: row.key }],
    )
    // D346 (d) — only on a tick landing while a query stands, including one made in the chooser.
    // The cap refuses a tick past `remaining` above, so a refused tick never clears the query.
    if (trimmed !== '') {
      setQuery('')
      searchInputRef.current?.focus()
    }
  }

  /**
   * **The checkbox's `onChange`, and on a multi-number row it does not tick.** The box is
   * controlled (`checked` is derived from the selection), so a change that updates nothing is
   * reverted by React on the next render: opening the chooser leaves the box unchecked, and a
   * dismissed chooser has nothing to undo. An *on* row unticks every number of its own that is
   * ticked — "tapping it unticks", and choosing again is a re-tick.
   */
  function onRowChange(row: BookRow, on: boolean): void {
    if (on) {
      setSelected((prev) => prev.filter((pick) => !row.phones.includes(pick.phone)))
      return
    }
    const eligible = eligibleOf(row)
    if (eligible.length === 0 || full) return
    if (eligible.length === 1) {
      tick(eligible[0]!, row)
      return
    }
    setChooserKey(row.key)
  }

  function removePicked(phone: E164): void {
    setSelected((prev) => prev.filter((pick) => pick.phone !== phone))
  }

  const chooserRow = chooserKey === undefined ? undefined : rows.find((row) => row.key === chooserKey)
  function closeChooser(): void {
    setChooserKey(undefined)
  }
  /*
   * D145 — the chooser's own entry, opened only from a tap on a row (a gesture), so it is legal
   * and lands ABOVE the book's: the hook is keyed separately, and the router's overlay stack closes
   * top-down. Back pops this one and `closeChooser` runs; the book's entry — owned by the screen,
   * not by this file — is untouched, and so are the ticks.
   */
  useDismissOnBack('invite:book-number', chooserRow !== undefined, closeChooser)

  function choose(row: BookRow, phone: E164): void {
    closeChooser()
    tick(phone, row)
  }

  async function shareMore(): Promise<void> {
    if (onShareMore === undefined || sharingMore) return
    setSharingMore(true)
    try {
      if (await onShareMore()) setQuery('')
    } finally {
      setSharingMore(false)
    }
  }

  return (
    <>
      {/*
        D346 (h) — search field and strip are one sticky block under the app bar,
        `overflow-x` only on the strip (D245 refuses a second `overflow-y`). `top` is computed
        rather than assumed: `.back` and `.bar-action` both set `min-block-size: var(--control-
        height)`, which is the app bar's own content-row floor, so the block's own padding plus
        that floor is where the bar actually ends.
      */}
      <div className="book-sticky">
        <div className="search-bar">
          <Icon name="search" size={15} />
          <input
            ref={searchInputRef}
            type="text"
            className="search-input"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder={t('bulkBookSearch')}
            aria-label={t('bulkBookSearch')}
            autoComplete="off"
            spellCheck={false}
          />
          {/*
            **No clear button.** Home's search bar has one and this one deliberately does not: the
            only string this namespace could label it with is `bulkBookCancel`, which names the
            control that leaves the list — an `aria-label` that lies to a screen reader about which
            button it is on is worse than a field the manager clears with backspace. A clear control
            here would need its own key, and D242 mints nine, not ten.
          */}
        </div>

        {/* D346 (h) — collapsed to nothing when empty, never a fixed-height placeholder. */}
        {selected.length > 0 ? (
          <div className="book-strip" ref={stripRef}>
            {selected.map((pick) => {
              const { phone } = pick
              const name = rowOf(pick)?.name
              return (
                <button
                  type="button"
                  className="book-chip"
                  key={phone}
                  onClick={() => removePicked(phone)}
                  aria-label={t('bulkBookRemovePicked', { name: name ?? phone })}
                >
                  <ContactAvatar {...(name === undefined ? {} : { name })} phone={phone} />
                  <span style={{ unicodeBidi: 'plaintext' }}>{name ?? phone}</span>
                </button>
              )
            })}
          </div>
        ) : null}

        {limited ? (
          <p className="hint">
            {t('bulkBookLimited')}
            {onShareMore === undefined ? null : (
              <>
                {' '}
                <LinkButton onClick={() => void shareMore()} disabled={sharingMore}>
                  {t('bulkBookOpenSettings')}
                </LinkButton>
              </>
            )}
          </p>
        ) : null}
      </div>

      {visible.length === 0 ? (
        <div className="row">
          <div className="l">{t('bulkBookEmpty')}</div>
        </div>
      ) : (
        visible.map((row) => {
          /* §4.1 — on when ANY of its numbers is ticked, and the second line then shows that one. */
          const onPhone = row.phones.find((phone) => chosen.has(phone))
          const on = onPhone !== undefined
          const eligible = eligibleOf(row)
          /*
           * D346 (g) — greyed only when EVERY number is on the gate, and never while ticked: a row
           * ticked before the roster landed stays ticked (and can still be unticked), rather
           * than changing under the manager's thumb.
           */
          const greyed = !on && eligible.length === 0
          const shown = onPhone ?? eligible[0] ?? row.phones[0]!
          const kind = kindOf(row, shown)
          const multi = row.phones.length > 1
          const numberCount = multi ? t('bulkBookNumberCount', { count: row.phones.length }) : undefined
          const header = headerStarts?.get(row.key)
          // §4.3 — shown only while the search is empty; drawing over a filtered view would
          // group survivors under headers that no longer describe a contiguous run.
          const showHeader = trimmed === '' && header !== undefined
          return (
            <div key={row.key}>
              {showHeader ? (
                <div className="book-header" aria-hidden="true">
                  {header}
                </div>
              ) : null}
              {/*
                **D250 — the whole row is the control, which is what Chrome's own picker does.** A
                `<label>` rather than a click handler on the `<div>`: the browser forwards the tap to
                the control it wraps, so there is no second toggle path to keep in step with the
                checkbox's own, keyboard focus stays on the input where a screen reader expects it,
                and a *disabled* input makes the whole row inert for free — at the cap the dead rows
                go dead across their full width rather than only under the box, which is the one
                place the withdrawn cap notice is still missed.

                `data-interactive` is what buys the pointer. The accessible name stays on the input,
                where an explicit `aria-label` outranks the label's own text, so wrapping the name and
                the number does not turn them into a second, longer name for the same control.
              */}
              <label className="row book-row" data-interactive="true">
                <div className="l">
                  {/*
                    **The avatar sits beside the text, scoped by `.book-row .l` rather than by the
                    shared `.row .l` rule** — every other `.row` in this app is a plain two-line
                    stack, and this file is the only one wearing an avatar, so the flex-row layout
                    that puts the two side by side lives under its own class rather than widening
                    what every other screen's list rows inherit.
                  */}
                  <ContactAvatar
                    {...(row.name === undefined ? {} : { name: row.name })}
                    phone={row.phones[0]!}
                  />
                  <div className="book-row-text">
                    {/*
                      **`unicode-bidi: plaintext`, inline.** The shell is `dir="rtl"` and an address
                      book routinely holds Latin names beside Hebrew ones; without it a Latin name
                      resolves against the RTL base direction and its punctuation walks to the wrong
                      end. Hebrew-only seed data hides this entire class of bug, which is how a
                      complete iOS pass once missed it.
                    */}
                    <div style={{ unicodeBidi: 'plaintext' }}>{row.name ?? shown}</div>
                    {/*
                      The second line: the number (unless it already is the first line), then its
                      type where the phone supplied one, then *Already on this gate* on a greyed
                      row. The number carries its own `dir="ltr"` so the line itself can follow
                      the shell's direction around a Hebrew type label.
                    */}
                    {row.name === undefined && kind === undefined && !greyed ? null : (
                      <div className="gsub">
                        {row.name === undefined ? null : <span dir="ltr">{shown}</span>}
                        {kind === undefined ? null : (
                          <span className="book-row-meta">{t(PHONE_KIND_KEYS[kind])}</span>
                        )}
                        {greyed ? <span className="book-row-meta">{t('bulkBookAlreadyMember')}</span> : null}
                      </div>
                    )}
                  </div>
                </div>
                <div className="r">
                  {/* §4.1 — the tag is decoration; the checkbox's own name says it (below). */}
                  {numberCount === undefined ? null : (
                    <span className="book-number-tag" aria-hidden="true">
                      {numberCount}
                    </span>
                  )}
                  {/*
                    **The accessible name is the contact, not the word "select".** A bare checkbox
                    in a list of two thousand reads as two thousand identical controls; the name is
                    the only thing that tells a screen reader which person is being ticked, and the
                    phone is what disambiguates two people with one name.

                  */}
                  <input
                    type="checkbox"
                    aria-label={[
                      row.name === undefined ? shown : `${row.name} ${shown}`,
                      numberCount,
                      greyed ? t('bulkBookAlreadyMember') : undefined,
                    ]
                      .filter((part) => part !== undefined)
                      .join(', ')}
                    checked={on}
                    /* Only the *unchecked* rows go dead at the cap — un-selecting must stay
                       available, or a manager who overshoots has no way back but Cancel. A
                       greyed row is dead for the same reason a full list is: there is nothing
                       left on it to tick. */
                    disabled={greyed || (!on && full)}
                    onChange={() => onRowChange(row, on)}
                  />
                </div>
              </label>
            </div>
          )
        })
      )}

      {chooserRow === undefined ? null : (
        <FrameOverlay>
          <NumberChooser
            title={t('bulkBookWhichNumber', { name: chooserRow.name ?? chooserRow.phones[0]! })}
            body={t('bulkBookWhichNumberBody')}
            choices={chooserRow.phones.map((phone) => {
              const kind = kindOf(chooserRow, phone)
              return { phone, ...(kind === undefined ? {} : { kind }), member: members.has(phone) }
            })}
            t={t}
            onChoose={(phone) => choose(chooserRow, phone)}
            onCancel={closeChooser}
          />
        </FrameOverlay>
      )}
    </>
  )
}
